import { ReactNode } from "react";
import { QueryResult as BaseQueryResult } from "@apollo/client";
import SunnySpinner from "ui/SunnySpinner";

interface UseQueryWrapperProps<QueryOptions, QueryResult> {
  useQuery: (option?: QueryOptions) => QueryResult;
  queryOptions?: QueryOptions;
  children?: ReactNode;
  displayData?: boolean;
}

/**
 * This is a handy utility to trigger a query in test. It simulates that a query has been
 * issued and result cached in Apollo cache. The cached data is ready for children component
 * to consume (eg viewer data).
 *
 * @param useQuery the query to be triggered before children is rendered
 * @param children the children to be rendered
 */
export default function UseQueryWrapper<QueryOptions, QueryResult>({
  useQuery,
  queryOptions,
  displayData,
  children,
}: UseQueryWrapperProps<QueryOptions, QueryResult>) {
  const { loading, error, data } = useQuery(queryOptions) as BaseQueryResult;

  if (loading) {
    return <SunnySpinner fullPage={true} />;
  }

  if (error || !data) {
    return <div>Error...</div>;
  }

  return (
    <>
      {children}
      {displayData && (
        <div data-testid="use-query-wrapper-data">
          {JSON.stringify(data, null, 2)}
        </div>
      )}
    </>
  );
}
